import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Which rows on the current page are ticked.
 *
 * Two rules make this behave the way a table full of financial records has to:
 *
 * **Only selectable rows are ever selected.** `canSelect` decides — a student
 * customer, an application invoice, a commission payout — and the header
 * checkbox picks the eligible rows, not all of them. Ticking twenty rows to
 * find out that fifteen were never deletable is not a bulk action, it is a
 * quiz.
 *
 * **Selection does not survive the rows changing.** Paging, filtering or a
 * reload prunes anything no longer on screen, so "Delete 6 selected" can never
 * mean six things you cannot see. This is why the keys are pruned against
 * `rows` rather than accumulated across pages.
 *
 * `keyOf` and `canSelect` are held in refs, so an inline arrow at the call
 * site does not re-run the pruning effect on every render.
 */
export default function useRowSelection(rows, { keyOf, canSelect } = {}) {
  const [keys, setKeys] = useState([]);
  const keyOfRef = useRef(keyOf);
  const canSelectRef = useRef(canSelect);
  keyOfRef.current = keyOf || ((row) => row?.id);
  canSelectRef.current = canSelect || (() => true);

  const list = useMemo(() => rows || [], [rows]);

  const selectable = useMemo(
    () => list.filter((row) => canSelectRef.current(row)),
    [list]
  );

  useEffect(() => {
    const present = new Set(selectable.map((row) => keyOfRef.current(row)));
    setKeys((current) => {
      const kept = current.filter((key) => present.has(key));
      return kept.length === current.length ? current : kept;
    });
  }, [selectable]);

  const selectedSet = useMemo(() => new Set(keys), [keys]);

  const selectedRows = useMemo(
    () => selectable.filter((row) => selectedSet.has(keyOfRef.current(row))),
    [selectable, selectedSet]
  );

  const isSelected = useCallback(
    (row) => selectedSet.has(keyOfRef.current(row)),
    [selectedSet]
  );

  const toggle = useCallback((row) => {
    const key = keyOfRef.current(row);
    setKeys((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    );
  }, []);

  const clear = useCallback(() => setKeys([]), []);

  const allSelected = selectable.length > 0 && selectedRows.length === selectable.length;

  const toggleAll = useCallback(() => {
    setKeys((current) =>
      current.length >= selectable.length
        ? []
        : selectable.map((row) => keyOfRef.current(row))
    );
  }, [selectable]);

  return {
    selectedRows,
    count: selectedRows.length,
    selectableCount: selectable.length,
    isSelected,
    toggle,
    toggleAll,
    allSelected,
    clear,
  };
}
